import { useState } from "react";
import {
  Box,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Typography,
} from "@mui/material";
import Markdown from "react-markdown";
import { useNavigate, useParams } from "react-router";

import type { TermsAndConditions } from "@thalamos/aspire-common";

import { api, apiHooks } from "~/api.js";
import {
  Button,
  LoadingSpinner,
  renderErrorToast,
  renderSuccessToast,
  TextField,
} from "~/components/design-system/index.js";
import { routeFns } from "~/routePaths.js";

function TermsAndConditionsCreateEditPageInner(props: {
  termsAndConditionsId: string;
  termsAndConditions: TermsAndConditions | null;
  refetchTermsAndConditions: () => void;
}) {
  const navigate = useNavigate();

  const isCreate = props.termsAndConditions === null;

  const [humanReadableName, setHumanReadableName] = useState(
    props.termsAndConditions?.humanReadableName ?? "",
  );

  const [markdown, setMarkdown] = useState(
    props.termsAndConditions?.markdown ?? "",
  );

  const [type, setType] = useState<TermsAndConditions["type"]>(
    props.termsAndConditions?.type ?? "data-processing-agreement",
  );

  return (
    <>
      <Typography variant="h3">
        Terms and Conditions - {isCreate ? "Add New" : "View"}
      </Typography>
      <TextField
        useFullWidth
        label="Internal Name"
        name="humanReadableName"
        disabled={!isCreate}
        value={humanReadableName}
        onChange={setHumanReadableName}
      />

      <FormControl fullWidth sx={{ my: "1em" }}>
        <InputLabel id="type-select-label">Type</InputLabel>
        <Select
          labelId="type-select-label"
          value={type}
          onChange={(e) =>
            setType(e.target.value as TermsAndConditions["type"])
          }
          disabled={!isCreate}
        >
          <MenuItem value="data-processing-agreement">
            Data Processing Agreement
          </MenuItem>
          <MenuItem value="terms-of-use">Terms of Use</MenuItem>
        </Select>
      </FormControl>
      <Box sx={{ width: "100%", display: "flex", gap: "10px" }}>
        <Box sx={{ width: "100%" }}>
          <TextField
            useFullWidth
            name="markdown"
            disabled={!isCreate}
            multiline
            value={markdown}
            rows="20"
            onChange={setMarkdown}
          />
        </Box>

        <Box
          sx={{ paddingLeft: "1em", border: "1px solid gray", width: "100%" }}
        >
          <Markdown>{markdown}</Markdown>
        </Box>
      </Box>
      {isCreate && (
        <Button
          label="Create"
          disabled={!humanReadableName.trim() || !markdown.trim()}
          onClick={async () => {
            const response = await api.termsAndConditions.create(
              props.termsAndConditionsId,
              {
                humanReadableName: humanReadableName.trim(),
                markdown: markdown.trim(),
                type: type,
              },
            );

            if (response.status === 204) {
              renderSuccessToast({
                message: "Terms and Conditions created successfully",
              });
            } else {
              renderErrorToast({
                message:
                  "Failed to create Terms and Conditions: " +
                  response.statusText,
              });
            }
            navigate(routeFns.termsAndConditionsManagementPage());
          }}
        />
      )}
    </>
  );
}

export function TermsAndConditionsCreateEditPage({}: {}) {
  let { termsAndConditionsId } = useParams();

  const [
    { data: termsAndConditions, loading: termsAndConditionsLoading, response },
    refetchTermsAndConditions,
  ] = apiHooks.termsAndConditions.get(termsAndConditionsId!);

  return termsAndConditionsLoading ? (
    <LoadingSpinner />
  ) : (
    <TermsAndConditionsCreateEditPageInner
      termsAndConditionsId={termsAndConditionsId!}
      termsAndConditions={response?.status === 200 ? termsAndConditions! : null}
      refetchTermsAndConditions={refetchTermsAndConditions}
    />
  );
}
